import { Plus } from "icons/font-awesome/regular"
import { Service } from "modules/api-client/src/services/types"
import { useServicesData } from "modules/cached-data"
import { groupBy } from "modules/helpers"
import { useTranslations } from "modules/translations"
import React, { ReactNode, useCallback, useMemo } from "react"
import CSVLinkButton from "ui-kit/elements/csv-link-button"
import Hyperlink from "ui-kit/elements/hyper-link"
import LinkButton from "ui-kit/elements/link-button"
import PageHeading from "ui-kit/headings/page-heading"
import InteractiveTable from "ui-kit/lists/interactive-table"

export default function ServicesIndex (): ReactNode {
    const { translate } = useTranslations()
    const { services } = useServicesData()
    const loadingServices = useServicesData().isLoading

    interface TableRow {
        id: number
        customer: string
        hostname: string
        domain: boolean
        dns: boolean
        hosting: boolean|string
        ssl: string
    }

    const groupServices = useCallback( ( services: Service[] ): TableRow[] => {
        const servicesWithName = services.map( ( data: Service ) => ( {
            ...data,
            name: data.type === "extension" ? data.domain : data.product_name,
        } ) )

        const groupedServices = groupBy<Service>( servicesWithName, service => service.domain )

        return Object.entries( groupedServices ).map( ( [key, entry], id ) => {
            const services = entry as Service[]
            const hostname = key as string

            let dns = true
            let domain = false
            let hosting: boolean|string = false
            let ssl = ""
            let customer = ""

            services.forEach( service => {
                customer = service.customer

                switch ( service.type ) {
                    case "extension":
                        domain = true
                        break
                    case "hosting":
                        hosting = service.product_name
                        break
                    case "ssl":
                        dns = false
                        ssl = ssl === "" ? service.product_name : `${ssl}, ${service.product_name}`
                        break
                }
            } )

            return {
                id,
                customer,
                hostname,
                domain,
                dns,
                hosting,
                ssl,
            } as TableRow
        } ).filter( service => service.dns || service.domain || service.hosting || service.ssl )
    }, [] )

    const servicesData = useMemo( () => {
        return services ? groupServices( services ) : []
    }, [services, groupServices] )

    return (
        <>
            <PageHeading text={translate( "pages.services.page_heading" )} id="services-heading">
                <CSVLinkButton data={servicesData} filename="data.csv">
                    {translate( "pages.services.button_download_csv" )}
                </CSVLinkButton>
                <LinkButton
                    href="/services/add-dns"
                    icon={Plus}
                >
                    {translate( "pages.services.button_add_dns" )}
                </LinkButton>
            </PageHeading>

            <InteractiveTable className="mt-5" labelledBy="services-heading"
                loading={loadingServices}
                columns={[
                    {
                        header: translate( "pages.services.table_header_customer" ),
                        property: "customer",
                    },
                    {
                        header: translate( "pages.services.table_header_hostname" ),
                        property: "hostname",
                    },
                    {
                        header: translate( "pages.services.table_header_dns" ),
                        property: "dns",
                        transform: dns => dns ? translate( "pages.services.table_element_yes" ) : translate( "pages.services.table_element_no" ),
                    },
                    {
                        header: translate( "pages.services.table_header_domain" ),
                        property: "domain",
                        transform: domain => domain ? translate( "pages.services.table_element_yes" ) : translate( "pages.services.table_element_no" ),
                    },
                    {
                        header: translate( "pages.services.table_header_hosting" ),
                        property: "hosting",
                        transform: hosting => hosting === false ? translate( "pages.services.table_element_no" ): hosting as string,
                    },
                    {
                        header: translate( "pages.services.table_header_ssl" ),
                        property: "ssl",
                    },
                    {
                        header: "",
                        property: "link",
                        sortable: false,
                        searchable: false,
                    },
                ]}
                data={
                    servicesData.map( ( data: TableRow ) => ( {
                        ...data,
                        link: <Hyperlink href={`/services/${data.hostname}/dns`}>{translate( "pages.services.table_element_manage" )}</Hyperlink>,
                    } ) )
                }
                keyProperty="id"
                searchable
            />
        </>
    )
}
